import React, { useEffect, useRef, useState } from "react";

// Card outline: rounded rectangle with a notch cut out of the bottom-right
// corner, where the arrow sits. Drawn per card size, since the notch must keep
// its size while the card stretches.
const RADIUS = 12;
const NOTCH = 64;
const NOTCH_RADIUS = 16;

const outline = (w, h) =>
    [
        `M ${RADIUS} 0`,
        `L ${w - RADIUS} 0`,
        `A ${RADIUS} ${RADIUS} 0 0 1 ${w} ${RADIUS}`,
        `L ${w} ${h - NOTCH - RADIUS}`,
        `A ${RADIUS} ${RADIUS} 0 0 1 ${w - RADIUS} ${h - NOTCH}`,
        `L ${w - NOTCH + NOTCH_RADIUS} ${h - NOTCH}`,
        `A ${NOTCH_RADIUS} ${NOTCH_RADIUS} 0 0 0 ${w - NOTCH} ${
            h - NOTCH + NOTCH_RADIUS
        }`,
        `L ${w - NOTCH} ${h - RADIUS}`,
        `A ${RADIUS} ${RADIUS} 0 0 1 ${w - NOTCH - RADIUS} ${h}`,
        `L ${RADIUS} ${h}`,
        `A ${RADIUS} ${RADIUS} 0 0 1 0 ${h - RADIUS}`,
        `L 0 ${RADIUS}`,
        `A ${RADIUS} ${RADIUS} 0 0 1 ${RADIUS} 0 Z`,
    ].join(" ");

const useElementSize = () => {
    const ref = useRef(null);
    const [size, setSize] = useState(null);

    useEffect(() => {
        const element = ref.current;
        if (!element || typeof ResizeObserver === "undefined") return;

        const update = () => {
            const width = Math.round(element.clientWidth);
            const height = Math.round(element.clientHeight);
            if (!width || !height) return;

            setSize((current) =>
                current?.width === width && current?.height === height
                    ? current
                    : { width, height }
            );
        };

        update();
        const observer = new ResizeObserver(update);
        observer.observe(element);

        return () => observer.disconnect();
    }, []);

    return [ref, size];
};

// Renders `${block}__frame` (measured), `${block}__fill` (clipped background)
// and `${block}__edge` (stroke). Styling lives with the parent block.
const NotchedOutline = ({ block }) => {
    const [ref, size] = useElementSize();
    const path = size ? outline(size.width, size.height) : null;
    const clipPath = path ? `path("${path}")` : undefined;

    return (
        <>
            {/* Parent links do not forward refs, so measure an inset frame */}
            <span className={`${block}__frame`} ref={ref} />
            <span
                className={`${block}__fill`}
                style={{ clipPath, WebkitClipPath: clipPath }}
            />
            {path && (
                <svg
                    className={`${block}__edge`}
                    viewBox={`-1 -1 ${size.width + 2} ${size.height + 2}`}
                    aria-hidden="true"
                >
                    <path d={path} />
                </svg>
            )}
        </>
    );
};

export const NotchArrowIcon = () => (
    <svg
        viewBox="0 0 22 22"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
    >
        <path d="M6.5 15.5 15.5 6.5" />
        <path d="M8.6 6.5h6.9v6.9" />
    </svg>
);

export default NotchedOutline;
